import { useState, useEffect } from "react";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import Alert from "../../app-components/alert/Alert";
import ActiveFilters, {
  FilterChip,
} from "../../app-components/ui/ActiveFilters";
import DateFilter from "../../app-components/ui/DateFilter";
import EmptyState from "../../app-components/ui/EmptyState";
import PageHeader from "../../app-components/ui/PageHeader";
import Pagination from "../../app-components/ui/Pagination";
import StatusPill from "../../app-components/ui/StatusPill";
import { btnPrimary, card, inputBase } from "../../app-components/ui/styles";
import TableSkeleton from "../../app-components/ui/TableSkeleton";
import { SubscriptionInterface } from "../../interface/subscription.interface";
import { ParamInterface } from "../../interface/users.interface";
import { formatDate } from "../../utils";

const TABS = [
  { value: "", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "PENDING", label: "Pending" },
  { value: "CANCELED", label: "Canceled" },
  { value: "CLOSED", label: "Closed" },
];

const PLAN_OPTIONS = [
  { value: "STANDARD", label: "Standard" },
  { value: "BRONZE", label: "Bronze" },
  { value: "SILVER", label: "Silver" },
  { value: "GOLD", label: "Gold" },
];

const th =
  "py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-hcMuted bg-hcHead sticky top-0 z-10 whitespace-nowrap";
const td = "py-3 px-4 text-left text-sm whitespace-nowrap";

const Subscriptions = () => {
  const [subscriptionData, setSubscriptionData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [pageFilter, setPageFilter] = useState<string>("");

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputDebounce, setSearchInputDebounce] = useState<string>("");
  const [dateRange, setDateRange] = useState<string>("");
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [hasError, setHasError] = useState<boolean>(false);

  // effect for get subscriptions list
  useEffect(() => {
    const params = {
      page: currentPage,
      status: pageFilter,
      search: searchInput,
      dateRange: dateRange,
      subscriptionPlan: subscriptionPlan,
    };

    getSubscription(params);
  }, [currentPage, pageFilter, searchInput, dateRange, subscriptionPlan]);

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputDebounce]);

  useEffect(() => {
    setAlertMessage("");

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end >= start) {
        setDateRange(startDate + "," + endDate);
        setCurrentPage(1);
      } else {
        setAlertMessage("Invalid date range.");
      }
    }
    if (!startDate && !endDate) {
      setDateRange("");
    }
  }, [startDate, endDate]);

  const getSubscription = async (params: ParamInterface) => {
    setLoading(true);
    setHasError(false);
    try {
      const res: any = await apiClient.get("subscription", { params });
      if (res && res.data) {
        setSubscriptionData(res.data.data);
        setCurrentPage(res.data.current_page);
        setLastPage(res.data.last_page);
        setTotal(res.data.total);
      } else {
        setSubscriptionData([]);
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      setAlertMessage("Unable to fetch record at the moment.");
      setHasError(true);
      setSubscriptionData([]);
    } finally {
      setLoading(false);
    }
  };

  const setDates = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const clearFilters = () => {
    setSearchInputDebounce("");
    setSearchInput("");
    setSubscriptionPlan("");
    setDates("", "");
    setCurrentPage(1);
  };

  // Chips for every filter currently applied
  const chips: FilterChip[] = [];
  if (searchInput)
    chips.push({
      label: `Search: ${searchInput}`,
      onRemove: () => {
        setSearchInputDebounce("");
        setSearchInput("");
        setCurrentPage(1);
      },
    });
  if (subscriptionPlan)
    chips.push({
      label: `Plan: ${
        PLAN_OPTIONS.find((p) => p.value === subscriptionPlan)?.label ??
        subscriptionPlan
      }`,
      onRemove: () => {
        setSubscriptionPlan("");
        setCurrentPage(1);
      },
    });
  if (dateRange)
    chips.push({
      label: `Date: ${startDate} to ${endDate}`,
      onRemove: () => setDates("", ""),
    });

  const hasRecords = subscriptionData?.length > 0;

  return (
    <AdminLayout title="Admin | Subscriptions">
      <PageHeader
        title="Subscriptions"
        subtitle="Plans members have subscribed to"
      />

      <div className={`${card} py-5`}>
        {/* tabs */}
        <div className="px-5 flex items-center gap-1 mb-5 border-b border-hcBorder">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setPageFilter(t.value);
                setCurrentPage(1);
              }}
              className={`px-4 pb-3 text-sm font-medium -mb-px border-b-2 transition ${
                pageFilter === t.value
                  ? "text-honeConnectPurple border-honeConnectPurple"
                  : "text-honeConnectGray border-transparent hover:text-honeConnectBlack"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* filter bar */}
        <div className="px-5 flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            </span>
            <input
              value={searchInputDebounce}
              onChange={(e) => setSearchInputDebounce(e.target.value)}
              type="text"
              className={`${inputBase} pl-9 w-full`}
              placeholder="Search by tranx id, name, amount paid"
            />
          </div>

          <select
            aria-label="Plan"
            className={inputBase}
            value={subscriptionPlan}
            onChange={(e) => {
              setSubscriptionPlan(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All plans</option>
            {PLAN_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="px-5 mb-4">
          <DateFilter
            startDate={startDate}
            endDate={endDate}
            onChange={setDates}
          />
        </div>

        <ActiveFilters chips={chips} onClearAll={clearFilters} />

        {/* record table */}
        {hasError && !loading ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center border-y border-hcBorder">
            <p className="font-semibold text-honeConnectBlack mb-1">
              Something went wrong
            </p>
            <p className="text-sm text-hcMuted mb-4">
              We couldn't load subscriptions. Please try again.
            </p>
            <button
              onClick={() =>
                getSubscription({
                  page: currentPage,
                  status: pageFilter,
                  search: searchInput,
                  dateRange: dateRange,
                  subscriptionPlan: subscriptionPlan,
                } as ParamInterface)
              }
              className={btnPrimary}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-auto max-h-[68vh] border-y border-hcBorder">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th
                    className={`${th} left-0 z-20`}
                    style={{ position: "sticky" }}
                  >
                    Name
                  </th>
                  <th className={th}>Email</th>
                  <th className={th}>Plan</th>
                  <th className={th}>Status</th>
                  <th className={th}>Start Date</th>
                  <th className={th}>End Date</th>
                </tr>
              </thead>

              {loading ? (
                <TableSkeleton columns={6} />
              ) : (
                <tbody className="text-honeConnectBlack">
                  {subscriptionData?.map((data: SubscriptionInterface) => (
                    <tr
                      key={data.id}
                      className="group border-b border-hcBorder hover:bg-hcBg"
                    >
                      <td
                        className={`${td} font-medium bg-white group-hover:bg-hcBg z-[1]`}
                        style={{ position: "sticky", left: 0 }}
                      >
                        {data.user.full_name ?? "No Name"}
                      </td>
                      <td className={td}>{data.user.email ?? "No Email"}</td>
                      <td className={td}>{data?.plan?.name}</td>
                      <td className={td}>
                        {data.status && <StatusPill label={data.status} />}
                      </td>
                      <td className={td}>
                        {data.created_at
                          ? formatDate(data.start_date, "date")
                          : ""}
                      </td>
                      <td className={td}>
                        {data.created_at
                          ? formatDate(data.end_date, "date")
                          : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>

            {!loading && !hasRecords && (
              <EmptyState
                title="No subscriptions found"
                message={
                  chips.length || pageFilter
                    ? "Try changing your search or filters."
                    : "There are no subscriptions to display yet."
                }
                onClear={chips.length ? clearFilters : undefined}
              />
            )}
          </div>
        )}

        {hasRecords && lastPage > 1 && (
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            total={total}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <Alert message={alertMessage} />
    </AdminLayout>
  );
};

export default Subscriptions;
